import { useEffect, useState } from "react";
import blinkscribeapi from "../api/blinkscribeapi";
import { bsAccessGated, getPracticeId } from "../config/practice";

/*
 * Does the signed-in user have BlinkScribe in THIS practice? (OSA batch 6,
 * owner decision 10-01: a practice whose profile lists bsAccessOnly items --
 * 7825 -- shows Note Review, Print Notes and Note instructions only to such a
 * user, and hides the chart mic / note panel from a user known to lack it.)
 *
 * One GET {bs}/auth/me per session and login: BlinkScribe resolves the care
 * token to its user in the token's practice -- 200 = access; 401 (no
 * BlinkScribe account) or 403 (an account with no grant in this practice) =
 * none. The request is sent with `_retry` set so the BlinkScribe client's 401
 * interceptor does not try a token refresh (a refresh would replace the
 * stored care token; BlinkScribe never expires care tokens, so a 401 here
 * only means "no account"). A definite answer is kept in sessionStorage,
 * keyed by practice and a hash of the token (a new login asks again); no
 * answer (network / 5xx) is not kept, so the next page load asks again.
 *
 * Values: "yes" | "no" | "unknown" (not answered yet / no answer / signed
 * out). Items that need access show on "yes" only; the chart mic hides on
 * "no" only.
 * A practice that does not gate (9928) never asks: always "yes".
 */

const STORE_PREFIX = "care.bsAccess:";
let inflight = null; // { key, promise }
let current = { key: null, value: "unknown" };
const listeners = new Set();

function tokenHash(token) {
  // djb2: a cache key only (never the token itself in storage).
  let h = 5381;
  for (let i = 0; i < token.length; i += 1) h = ((h << 5) + h + token.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

function storageKey() {
  let token = "";
  try {
    token = localStorage.getItem("auth") || "";
  } catch (e) {
    token = "";
  }
  if (!token) return null;
  return `${STORE_PREFIX}${getPracticeId()}:${tokenHash(token)}`;
}

function readStored(key) {
  try {
    const v = sessionStorage.getItem(key);
    return v === "yes" || v === "no" ? v : null;
  } catch (e) {
    return null;
  }
}

function writeStored(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {
    // storage blocked: the in-memory answer still holds for this page load
  }
}

function publish(key, value) {
  current = { key, value };
  listeners.forEach((fn) => fn(value));
}

// The current answer without asking ("unknown" until a check has answered).
export function bsAccessNow() {
  if (!bsAccessGated()) return "yes";
  const key = storageKey();
  if (!key) return "unknown"; // signed out: nothing to ask with
  if (current.key === key && current.value !== "unknown") return current.value;
  return readStored(key) || "unknown";
}

// Ask once (per session + login); resolves to "yes" | "no" | "unknown".
export function checkBsAccess() {
  if (!bsAccessGated()) return Promise.resolve("yes");
  const key = storageKey();
  if (!key) return Promise.resolve("unknown");
  const known = bsAccessNow();
  if (known !== "unknown") {
    if (current.key !== key || current.value !== known) publish(key, known);
    return Promise.resolve(known);
  }
  if (inflight && inflight.key === key) return inflight.promise;
  const promise = blinkscribeapi
    .get("/auth/me", { _retry: true })
    .then(
      () => "yes",
      (err) => {
        const status = err?.response?.status;
        return status === 401 || status === 403 ? "no" : "unknown";
      },
    )
    .then((value) => {
      if (inflight && inflight.key === key) inflight = null;
      if (value !== "unknown") writeStored(key, value);
      publish(key, value);
      return value;
    });
  inflight = { key, promise };
  return promise;
}

// React: the answer, re-rendering when it arrives. Starts the check.
export function useBsAccess() {
  const [value, setValue] = useState(bsAccessNow);
  useEffect(() => {
    if (!bsAccessGated()) return undefined;
    let alive = true;
    const onChange = (v) => {
      if (alive) setValue(v);
    };
    listeners.add(onChange);
    checkBsAccess().then(onChange);
    return () => {
      alive = false;
      listeners.delete(onChange);
    };
  }, []);
  return value;
}

// Test hook: forget the in-memory answer (sessionStorage is the test's).
export function resetBsAccessForTests() {
  inflight = null;
  current = { key: null, value: "unknown" };
  listeners.clear();
}
